import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { captureSignupAttributionFromUrl } from "./data/signupAttribution";
import { installTenantScopeSubscriber } from "./lib/tenantCache";
import { auditLocalStorageUsage } from "./lib/localOnlyKeys";

// Capture signup attribution (?ref / ?staff / ?campaign / utm_*) on first
// landing so it persists through to account creation.
captureSignupAttributionFromUrl();

// Phase 7 — wipe tenant-scoped localStorage on any tenant-scope change so
// a Store Manager admin/storefront never inherits cached main-site data.
installTenantScopeSubscriber();

// Phase B — dev-time warning if any localStorage key is being used as a
// source of truth (i.e. not in SYNC_KEYS and not in LOCAL_ONLY_KEYS).
if (import.meta.env?.DEV) {
  setTimeout(() => {
    import("./data/cloudSync").then((m: any) => {
      try { auditLocalStorageUsage(m.getSyncKeys?.() ?? []); } catch { /* noop */ }
    }).catch(() => { /* noop */ });
  }, 2000);
}

// Clear stale/session-only auth sessions on cold start to prevent navigator lock deadlocks.
// If the stored refresh token can't be refreshed (e.g. network was down), the
// Supabase client will hang indefinitely trying to acquire the navigator lock.
try {
  const authKey = "sb-jialddxkojvhbstbsnft-auth-token";
  const rememberKey = "khays_lite_remember_me";
  const sessionOnlyKey = "khays_lite_session_active";
  if (localStorage.getItem(rememberKey) === "0" && !sessionStorage.getItem(sessionOnlyKey)) {
    localStorage.removeItem(authKey);
  }
  if (localStorage.getItem(rememberKey) === "0") {
    sessionStorage.setItem(sessionOnlyKey, "1");
  }
  const stored = localStorage.getItem(authKey);
  if (stored) {
    const parsed = JSON.parse(stored);
    const expiresAt = parsed?.expires_at;
    // If token expired more than 1 hour ago, clear it to prevent lock issues
    if (expiresAt && expiresAt < Math.floor(Date.now() / 1000) - 3600) {
      localStorage.removeItem(authKey);
      console.warn("Cleared expired auth session to prevent lock contention");
    }
  }
} catch {}

// Register the push service worker so customers can opt into notifications.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => { /* silent */ });
  });
}

createRoot(document.getElementById("root")!).render(<App />);
